import { createContext, useContext, useEffect, useState, useCallback } from "react";

const THEMES = ["light", "dark", "hc-light", "hc-dark"];
const ThemeContext = createContext(null);

function getInitial() {
  if (typeof window === "undefined") return "light";
  const saved = localStorage.getItem("fc_theme");
  if (saved && THEMES.includes(saved)) return saved;
  const prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
  return prefersDark ? "dark" : "light";
}

function applyTheme(theme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  const isDark = theme === "dark" || theme === "hc-dark";
  root.classList.toggle("dark", isDark);
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitial);
  const [isExplicit, setIsExplicit] = useState(() => {
    if (typeof window === "undefined") return false;
    const saved = localStorage.getItem("fc_theme");
    return !!(saved && THEMES.includes(saved));
  });

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Follow OS preference changes only when the user hasn't explicitly chosen a theme.
  useEffect(() => {
    if (isExplicit || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setThemeState(e.matches ? "dark" : "light");
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [isExplicit]);

  const setTheme = useCallback((t) => {
    if (!THEMES.includes(t)) return;
    try {
      localStorage.setItem("fc_theme", t);
    } catch (e) {
      /* ignore */
    }
    setIsExplicit(true);
    setThemeState(t);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
